import moment from "moment";
import i18n from "../i18n";
import crypto from "crypto";
import turkishToEnglish from "../constants/turkishToEnglish";

export const b64EncodeUnicode = str => {
    return btoa(
        encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function toSolidBytes(match, p1) {
            return String.fromCharCode("0x" + p1);
        })
    );
};

export const b64DecodeToUrl = async b64 => {
    const byteCharacters = atob(b64);
    const byteNumbers = await new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = await new Uint8Array(byteNumbers);
    const blob = await new Blob([byteArray]);

    const url = await window.URL.createObjectURL(blob);
    return url;
};

export const formatPhoneNumber = (str, placeholder = "-") => {
    const cleaned = ("" + str).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return match[1] + "(" + match[2] + ") " + match[3] + "-" + match[4];
    }
    return placeholder;
};

export const formatMomentDate = date => {
    return date ? moment(date).format("DD-MM-YYYY HH:mm:ss") : "";
};

export const constantFind = (array, value) => {
    const found = array && array.length && array.find(arr => arr.value === value);
    return found ? found.name : "-";
};

export const isConstantExist = (array, value, fieldName = "value") => {
    const found = array && array.length && array.find(arr => arr[fieldName] === value);
    return !!found;
};

export const getConstant = (array, value) => {
    const found = array && array.length && array.find(arr => arr.value === value);
    return found;
};

export const arrayAlphabeticalSort = (array, key = "name") => {
    const sorted = Array.isArray(array)
        ? array.sort((a, b) => {
              return a[key]
                  ? ["tümü", "all"].includes(a[key].toLowerCase()) ||
                    ["tümü", "all"].includes(b[key].toLowerCase())
                      ? 1
                      : a[key].localeCompare(b[key], "tr")
                  : 1;
          })
        : [];
    return sorted;
};

export const arrayNaturalSort = (array, key = "name") => {
    const sorted = Array.isArray(array)
        ? array.sort((a, b) =>
              a[key].localeCompare(b[key], undefined, {
                  numeric: true,
                  sensitivity: "base"
              })
          )
        : [];
    return sorted;
};

export const intIncSort = array => {
    const sorted = Array.isArray(array)
        ? [...array].sort((prev, curr) => prev.index - curr.index)
        : [];
    return sorted;
};

export const calculateDuration = ({
    startDate,
    endDate,
    dayUnit = "g",
    hourUnit = "s",
    minuteUnit = "dk",
    secondUnit = "sn"
}) => {
    const duration = Math.abs(new Date(startDate) - new Date(endDate));
    const seconds = Math.floor((duration / 1000) % 60),
        minutes = Math.floor((duration / (1000 * 60)) % 60),
        hours = Math.floor((duration / (1000 * 60 * 60)) % 24),
        days = Math.floor((duration / (1000 * 60 * 60 * 24)) % 30);
    return `${days ? days + dayUnit + " " : ""}${hours ? hours + hourUnit + " " : ""}${
        minutes ? minutes + minuteUnit + " " : ""
    }${seconds || seconds === 0 ? seconds + secondUnit + " " : ""}`;
};

export const decodePolyline = encoded => {
    const points = [];
    let index = 0,
        len = encoded.length;
    let lat = 0,
        lng = 0;
    while (index < len) {
        var b,
            shift = 0,
            result = 0;
        do {
            b = encoded.charAt(index++).charCodeAt(0) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);

        const dlat = (result & 1) != 0 ? ~(result >> 1) : result >> 1;
        lat += dlat;
        shift = 0;
        result = 0;
        do {
            b = encoded.charAt(index++).charCodeAt(0) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);
        const dlng = (result & 1) != 0 ? ~(result >> 1) : result >> 1;
        lng += dlng;

        points.push({lat: lat / 1e5, lng: lng / 1e5});
    }
    return points;
};

export const diffObject = (obj1, obj2) => {
    if (!obj1 || !obj2) return {};
    const diff = {};
    const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

    for (const key of allKeys) {
        const val1 = obj1[key];
        const val2 = obj2[key];

        if (val1 !== val2) {
            diff[key] = val2;
        }
    }

    return diff;
};

export const generateSecurePassword = (length = 12) => {
    if (length <= 3) {
        throw new Error("Password length must be greater than 3");
    }
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const digits = "0123456789";
    const specialCharacter = "#$%&'()*+,-./:;<=>?@[]^_`{|}~";
    const all = lower + upper + digits + specialCharacter;

    function getRandomInt(max) {
        const randomBytes = crypto.randomBytes(4);
        const randomNum = randomBytes.readUInt32BE(0);
        return randomNum % max;
    }

    function getRandomChar(charset) {
        return charset[getRandomInt(charset.length)];
    }

    function shuffleArray(array) {
        for (let i = array.length - 1; i > 0; i--) {
            const j = getRandomInt(i + 1);
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    }

    let password = [
        getRandomChar(lower),
        getRandomChar(upper),
        getRandomChar(digits),
        getRandomChar(specialCharacter)
    ];

    while (password.length <= length) {
        password.push(getRandomChar(all));
    }

    password = shuffleArray(password);

    return password.join("");
};

export const averageGeolocation = coords => {
    if (coords.length === 1) {
        return coords[0];
    }

    let x = 0.0;
    let y = 0.0;
    let z = 0.0;

    for (let coord of coords) {
        let latitude = (coord.latitude * Math.PI) / 180;
        let longitude = (coord.longitude * Math.PI) / 180;

        x += Math.cos(latitude) * Math.cos(longitude);
        y += Math.cos(latitude) * Math.sin(longitude);
        z += Math.sin(latitude);
    }

    let total = coords.length;

    x = x / total;
    y = y / total;
    z = z / total;

    let centralLongitude = Math.atan2(y, x);
    let centralSquareRoot = Math.sqrt(x * x + y * y);
    let centralLatitude = Math.atan2(z, centralSquareRoot);

    return {
        lat: (centralLatitude * 180) / Math.PI,
        lng: (centralLongitude * 180) / Math.PI
    };
};

export const handleCheckBoxText = (upperGroup, regionGroupsNames, searchText, city) => {
    let text = "";
    if (city && city !== "all") {
        text = city[0].toUpperCase() + city.slice(1);
        text = text.concat(" " + i18n.t("helpers.handleCheckBoxText.city", {ns: "common"}));
        if (upperGroup !== "") {
            text = text.concat(
                ", ",
                upperGroup,
                " " + i18n.t("helpers.handleCheckBoxText.uppergroup", {ns: "common"})
            );
            if (regionGroupsNames.length > 0) {
                regionGroupsNames.map(regionGroupName => {
                    text = text.concat(", ", regionGroupName.children);
                });
                if (regionGroupsNames.length === 1) {
                    if (searchText !== "") {
                        text = i18n.t(
                            "helpers.handleCheckBoxText.applyToAllSubcompaniesFilteredWith",
                            {
                                ns: "common",
                                searchText:
                                    text +
                                    " " +
                                    i18n.t("helpers.handleCheckBoxText.regionGroup", {
                                        ns: "common"
                                    }) +
                                    ", " +
                                    searchText
                            }
                        );
                    } else {
                        text = i18n.t(
                            "helpers.handleCheckBoxText.applyToSubcompaniesFilteredWith",
                            {
                                ns: "common",
                                text:
                                    text +
                                    " " +
                                    i18n.t("helpers.handleCheckBoxText.regionGroup", {ns: "common"})
                            }
                        );
                    }
                } else {
                    if (searchText !== "") {
                        text = i18n.t(
                            "helpers.handleCheckBoxText.applyToAllSubcompaniesFilteredWith",
                            {
                                ns: "common",
                                searchText:
                                    text +
                                    " " +
                                    i18n.t("helpers.handleCheckBoxText.regionGroups", {
                                        ns: "common"
                                    }) +
                                    ", " +
                                    searchText
                            }
                        );
                    } else {
                        text = i18n.t(
                            "helpers.handleCheckBoxText.applyToSubcompaniesFilteredWith",
                            {
                                ns: "common",
                                text:
                                    text +
                                    " " +
                                    t("helpers.handleCheckBoxText.regionGroups", {ns: "common"})
                            }
                        );
                    }
                }
            } else {
                if (searchText !== "") {
                    text = i18n.t("helpers.handleCheckBoxText.applyToAllSubcompaniesFilteredWith", {
                        ns: "common",
                        searchText: text + ", " + searchText
                    });
                } else {
                    text = i18n.t("helpers.handleCheckBoxText.applyToSubcompaniesFilteredWith", {
                        ns: "common",
                        text: text
                    });
                }
            }
        } else {
            if (searchText !== "") {
                text = i18n.t("helpers.handleCheckBoxText.applyToAllSubcompaniesFilteredWith", {
                    ns: "common",
                    searchText: text + ", " + searchText
                });
            } else {
                text = i18n.t("helpers.handleCheckBoxText.applyToSubcompaniesFilteredWith", {
                    ns: "common",
                    text: text
                });
            }
        }
    } else {
        if (searchText !== "") {
            text = i18n.t("helpers.handleCheckBoxText.applyToAllSubcompaniesFilteredWith", {
                ns: "common",
                searchText: searchText
            });
        } else {
            text = i18n.t("helpers.handleCheckBoxText.applyAllSubcompanies", {ns: "common"});
        }
    }

    return text;
};

export const isEmptyObject = obj => {
    if (typeof obj === "object" && obj != null && Object.keys(obj).length !== 0) {
        return false;
    } else {
        return true;
    }
};

export const removeNullEntries = params => {
    let queryParams = {};
    Object.entries(params)
        .filter(
            entry =>
                !(
                    entry[1] === null ||
                    entry[1] === "" ||
                    entry[1] === undefined ||
                    (Array.isArray(entry[1]) && entry[1].length === 0)
                )
        )
        .forEach(entry => {
            queryParams[entry[0]] = entry[1];
        });
    return queryParams;
};

export const toMinute = (second: Number) => {
    return (second / 60).toFixed(2);
};

export const minuteToString = (minutes, showMinuteShort = true) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
        return `${hours} ${i18n.t("units.hour.short", {
            ns: "common"
        })} ${remainingMinutes} ${i18n.t("units.minute.short", {ns: "common"})}`;
    } else {
        return `${remainingMinutes} ${
            showMinuteShort ? i18n.t("units.minute.short", {ns: "common"}) : ""
        }`;
    }
};

export const minuteToStringForAddShift = (minutes, showMinuteShort = true) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0) {
        return `${hours} ${
            remainingMinutes !== 0
                ? i18n.t("units.hour.short", {
                      ns: "common"
                  })
                : i18n.t("units.hour.long", {
                      ns: "common"
                  })
        } ${remainingMinutes !== 0 ? remainingMinutes : ""} ${
            remainingMinutes !== 0 ? i18n.t("units.minute.short", {ns: "common"}) : ""
        }`;
    } else {
        return `${remainingMinutes} ${
            showMinuteShort ? i18n.t("units.minute.long", {ns: "common"}) : ""
        }`;
    }
};

export const encodeUriAll = value => {
    return value.replace(
        /[^A-Za-z0-9]/g,
        c =>
            `%${c
                .charCodeAt(0)
                .toString(16)
                .toUpperCase()}`
    );
};

export const truncateString = (str, num) => {
    // If the length of str is less than or equal to num
    // just return str--don't truncate it.
    if (str.length <= num) {
        return str;
    }
    // Return str truncated with '...' concatenated to the end of str.
    return str.slice(0, num) + "...";
};

export const normalizeString = str => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};
export const compareString = (str = "", searchStr = "") => {
    return normalizeString(str)
        .toLowerCase()
        .includes(normalizeString(searchStr).toLowerCase());
};

export const getLocaleLang = ({resolvedLanguage = "tr"}) =>
    resolvedLanguage === "en" ? "en" : "tr";

export const compareArrays = (array1, array2) => {
    for (let item of array1) {
        if (!array2.includes(item)) {
            return false;
        }
    }
    return true;
};

export const generateRandomOrderNumber = () => {
    return moment().format("mmssSSS");
};

export const calculateElapsedTime = (startDateString, endDateString) => {
    const startDate = moment(startDateString);
    const endDate = moment(endDateString);

    const diff = moment.duration(endDate.diff(startDate));

    const hours = Math.floor(diff.asHours());
    const minutes = Math.floor(diff.asMinutes()) % 60;
    const seconds = diff.seconds();

    const elapsedTime = `${hours}:${minutes}:${seconds}`;

    return elapsedTime;
};

export const toTurkishLowerCase = str => {
    return str
        .replace(/I/g, "ı")
        .replace(/İ/g, "i")
        .toLocaleLowerCase("tr");
};

export const toTurkishUpperCase = str => {
    return str
        .replace(/ı/g, "I")
        .replace(/i/g, "İ")
        .toLocaleUpperCase("tr");
};

export const formatNumberString = number => {
    if (number === undefined || number === null) return "####";
    const convertedNumber = number.toString().replace(",", ".");
    if (isNaN(Number(convertedNumber))) return "####";
    const fixedNumber = Number(convertedNumber).toFixed(2);
    return Number(fixedNumber).toLocaleString("tr-TR");
};

export const isNumeric = value => {
    return !isNaN(parseInt(value)) && !isNaN(parseFloat(value)) && isFinite(value);
};

export const normalizeText = text => {
    if (!text) return "";

    let normalized = text.toLowerCase();

    normalized = turkishToEnglish(normalized);

    normalized = normalized.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    return normalized;
};
